import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { MENU, CONTACT, PULL_QUOTE, LOGO } from "@/data/content";
import { postJSON } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await postJSON("/newsletter", { email, company });
      toast.success("You're subscribed. Welcome to the movement.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#050B14] border-t border-white/10 pt-20 pb-10" data-testid="footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <p className="font-display text-2xl md:text-4xl lg:text-5xl text-white/85 max-w-5xl leading-[1.15] tracking-tight">
          {PULL_QUOTE}
        </p>

        <div className="grid md:grid-cols-12 gap-10 mt-20 pt-10 border-t border-white/10">
          <div className="md:col-span-4">
            <span className="bg-[#ffffff] rounded-lg px-3 py-2 inline-flex">
              <img src={LOGO} alt="THECREATOR Education" className="h-11 w-auto" />
            </span>
            <p className="text-white/50 text-sm mt-4 max-w-xs leading-relaxed">
              Where future researchers are nurtured. A K–12 STEM framework built on problem solving.
            </p>
            <a
              href="https://www.the-creator.net"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-school-link"
              className="inline-flex items-center gap-1.5 mt-5 text-accent hover:text-white transition-colors text-sm"
            >
              Visit The Creator International School
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono-label text-[11px] tracking-label uppercase text-white/40 mb-4">Explore</p>
            <ul className="space-y-2">
              {MENU.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="text-white/70 hover:text-accent transition-colors text-sm">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="font-mono-label text-[11px] tracking-label uppercase text-white/40 mb-4">Connect</p>
            <ul className="space-y-2">
              {CONTACT.socials.map((s) => (
                <li key={s.label}>
                  <a href={s.href} target="_blank" rel="noreferrer" className="text-white/70 hover:text-accent transition-colors text-sm inline-flex items-center gap-1">
                    {s.label} <ArrowUpRight className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono-label text-[11px] tracking-label uppercase text-white/40 mb-4">Newsletter</p>
            <form onSubmit={subscribe} className="flex items-center border-b border-white/20 focus-within:border-accent transition-colors" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email-input"
                className="bg-transparent text-white placeholder:text-white/30 text-sm py-2 flex-1 outline-none"
              />
              <input type="text" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} className="hidden" aria-hidden="true" />
              <button type="submit" disabled={loading} data-testid="newsletter-submit" className="text-accent disabled:opacity-50">
                <ArrowUpRight className="w-5 h-5" />
              </button>
            </form>
            <div className="mt-6 space-y-1 text-sm text-white/60">
              <a href={`mailto:${CONTACT.email}`} className="block hover:text-accent">{CONTACT.email}</a>
              <a href={`tel:${CONTACT.phone}`} className="block hover:text-accent">{CONTACT.phone}</a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 font-mono-label text-[11px] tracking-label uppercase text-white/40">
          <span>© {new Date().getFullYear()} THECREATOR Education</span>
          <span>Problem Solving Must Be the Curriculum</span>
        </div>
      </div>
    </footer>
  );
}
